/**
 * Short labels for a date range, for DateRangeNav on phones: one line, no
 * repeated month, year only when it isn't this year.
 *
 *   Sep 24–28 · Sep 29 – Oct 3 · Dec 29 – Jan 4 · Sep 24–28, 2025
 *
 * Dates are ISO `YYYY-MM-DD` strings (inclusive) and are read as calendar
 * dates, so the viewer's time zone never shifts them.
 */

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function parts(date: string) {
  const [year, month, day] = date.split('-').map(Number);
  return { year, month: MONTHS[month - 1], day };
}

/**
 * The range without a year, plus the end's year when it isn't the current one
 * (null otherwise). `today` (ISO date) defaults to the local date.
 */
export function shortDateRangeParts(
  start: string,
  end: string,
  today?: string,
): { range: string; year: number | null } {
  const s = parts(start);
  const e = parts(end);
  const currentYear = today ? parts(today).year : new Date().getFullYear();
  const year = e.year === currentYear ? null : e.year;

  if (start === end) return { range: `${s.month} ${s.day}`, year };
  if (s.year === e.year && s.month === e.month) {
    return { range: `${s.month} ${s.day}–${e.day}`, year };
  }
  return { range: `${s.month} ${s.day} – ${e.month} ${e.day}`, year };
}

export function formatShortDateRange(start: string, end: string, today?: string): string {
  const { range, year } = shortDateRangeParts(start, end, today);
  return year === null ? range : `${range}, ${year}`;
}

/** A single day for the phone header: `Mon, Sep 28` (the year goes elsewhere, see `shortDateRangeParts`). */
export function formatShortDay(date: string): string {
  const { year, month, day } = parts(date);
  const weekday = WEEKDAYS[new Date(Date.UTC(year, MONTHS.indexOf(month), day)).getUTCDay()];
  return `${weekday}, ${month} ${day}`;
}

/** "Today", "Yesterday" or "3 days ago" for a day on or before `today`. */
export function relativeDayLabel(daysAgo: number): string {
  if (daysAgo <= 0) return 'Today';
  return daysAgo === 1 ? 'Yesterday' : `${daysAgo} days ago`;
}
